export type RiskLevel = "low" | "moderate" | "high";

const LEVEL_RANK: Record<RiskLevel, number> = { low: 0, moderate: 1, high: 2 };

/**
 * Maps a non-negative measurement to a [0, 1] severity score, banded so that:
 *   - 0..0.33  →  low band     (value 0..moderate threshold)
 *   - 0.33..0.66 → moderate band (value moderate..high threshold)
 *   - 0.66..1.0 → high band     (value high..2*high threshold, then capped)
 * Returns 0 for non-finite or negative inputs.
 */
export function bandedSeverity(value: number, moderate: number, high: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  if (moderate <= 0 || high <= moderate) return 0;
  if (value <= moderate) return (value / moderate) * 0.33;
  if (value <= high) return 0.33 + ((value - moderate) / (high - moderate)) * 0.33;
  return Math.min(1, 0.66 + ((value - high) / high) * 0.34);
}

export function severityToLevel(severity: number): RiskLevel {
  if (severity >= 0.66) return "high";
  if (severity >= 0.33) return "moderate";
  return "low";
}

export function severityToPercentage(severity: number): number {
  return Math.min(100, Math.max(0, Math.round(severity * 100)));
}

export function worstLevel(levels: Array<RiskLevel | null | undefined>): RiskLevel {
  return levels.reduce<RiskLevel>(
    (acc, l) => (l && LEVEL_RANK[l] > LEVEL_RANK[acc] ? l : acc),
    "low",
  );
}
